import { cn } from '@repo/tailwind-config/tailwind.functions'
import { FileTypeEnum } from '@repo/toolkit/types'
import Button from '@repo/ui/button'
import { DisplayImage } from '@repo/ui/file-input'
import { PdfIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'sonner'

import { getEnrollment } from '../../../api/enrollments/get-enrollment'
import { unlockChildInfo } from '../../../api/enrollments/information-locks'
import { validateEnrollmentChildInfo } from '../../../api/enrollments/validate-enrollment-child-info'
import useAuth from '../../../lib/hooks/use-auth'
import { useDataProps } from '../../../lib/hooks/use-data-props'
import useTranslate from '../../../lib/hooks/use-translate'
import { EnrollmentInformationStatus } from '../../../lib/types/enums/enrollment/enrollment-information-status.enum'
import { RoleEnum } from '../../../lib/types/enums/users/role.enum'
import {
  enrollmentChildInfoStatusFields,
  EnrollmentChildInfoStatusType
} from '../../../lib/types/fields/enrollments/enrollment'
import { DataProps } from '../../../lib/types/utils'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { ItemNotFound } from '../../elements/item-not-found'
import LoadingScreen from '../../elements/loading/loading-screen'

type Props = {
  src: string
  isOpen: boolean
  onCancel: () => void
  onEnrollmentUpdate?: () => void
}

type EnrollmentInformationStatusPermissions = {
  canValidate: boolean
  canUnlock: boolean
}

const enrollmentInformationStatusPermissions: Record<
  EnrollmentInformationStatus,
  EnrollmentInformationStatusPermissions
> = {
  PENDING: { canValidate: false, canUnlock: false },
  TO_BE_COMPLETED: { canValidate: true, canUnlock: true },
  COMPLETED: { canValidate: false, canUnlock: false }
}

export function EnrollmentProfileModel(props: DataProps<EnrollmentChildInfoStatusType, Props>) {
  const { t } = useTranslate()
  const { user } = useAuth()
  const {
    data: enrollment,
    isPending,
    isOpen,
    src,
    onCancel,
    onEnrollmentUpdate
  } = useDataProps(props, (id) => getEnrollment(id, enrollmentChildInfoStatusFields))

  const unlock = useMutation({
    mutationFn: unlockChildInfo,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Enfant débloqué avec succès')
      onEnrollmentUpdate?.()
      onCancel()
    }
  })

  const validate = useMutation({
    mutationFn: validateEnrollmentChildInfo,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Enfant validé avec succès')
      onEnrollmentUpdate?.()
      onCancel()
    }
  })

  if (isPending) return <LoadingScreen />
  if (!enrollment) return <ItemNotFound title={t('enrollment.notFound')} />

  const actionsPermissions = enrollmentInformationStatusPermissions[enrollment.childInfoStatus]
  return (
    <Modal
      hideActions
      size="large"
      className="!h-[95vh] max-h-[95vh]"
      icon={PdfIcon}
      isOpen={isOpen}
      onCancel={onCancel}
      title={t('enrollment.profile.pdf.title')}
    >
      <div className={cn(MODAL_CONTENT_CLASSNAME, 'flex flex-col gap-4')}>
        <DisplayImage allowAll type={FileTypeEnum.PDF} src={src} className="h-[75dvh]" />

        <div className="flex flex-row gap-2 justify-end">
          <Button variant={'ghost'} label={t('enrollment.profile.pdf.close')} onClick={onCancel} />

          {[RoleEnum.ADMIN].includes(user.role) && (
            <>
              <Button
                label={t('enrollment.profile.pdf.unlock')}
                variant={'destructive'}
                className={cn({ hidden: !actionsPermissions.canUnlock })}
                onClick={async () => await unlock.mutateAsync(enrollment.id)}
              />
              <Button
                label={t('enrollment.profile.pdf.validate')}
                variant={'primary'}
                className={cn({ hidden: !actionsPermissions.canValidate })}
                onClick={() => validate.mutateAsync(enrollment.id)}
              />
            </>
          )}
        </div>
      </div>
    </Modal>
  )
}
